import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, BookOpenIcon } from 'lucide-react';
interface LearnMoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSupport: () => void;
}
const allocations = [{
  title: 'Education & Scholarship',
  percentage: 40,
  description: 'Funding academic research, university chairs, and student fellowships.'
}, {
  title: 'Historical Preservation',
  percentage: 35,
  description: 'Digitizing archives and translating vast literary works into English.'
}, {
  title: 'Modern Policy Research',
  percentage: 25,
  description: 'Supporting think tanks applying the Iron Wall doctrine today.'
}];
const readings = [{
  title: 'The Iron Wall',
  year: '1923',
  type: 'Essay',
  description: 'The foundational text arguing that peace can only come when adversaries accept the permanence of Jewish self-determination.'
}, {
  title: 'The Ethics of the Iron Wall',
  year: '1923',
  type: 'Essay',
  description: 'The moral companion to The Iron Wall, defending the ethical right of a dispossessed people to reclaim their homeland.'
}, {
  title: 'The Idea of Betar',
  year: '1934',
  type: 'Essay',
  description: 'A manifesto defining the principles of Hadar—dignity, discipline, and national pride—as the foundation of the Betar youth movement.'
}, {
  title: 'Instead of Excessive Apology',
  year: '1911',
  type: 'Essay',
  description: 'An early and defiant rejection of Jewish self-blame, insisting on pride and the refusal to apologize for national existence.'
}];
export function LearnMoreModal({
  isOpen,
  onClose,
  onOpenSupport
}: LearnMoreModalProps) {
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  // Handle Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);
  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      y: 20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      y: 20,
      transition: {
        duration: 0.2
      }
    }
  };
  return <AnimatePresence data-id="element-74">
      {isOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-75">
          {/* Overlay */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" data-id="element-76" />

          {/* Modal Card */}
          <motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit" className="relative w-full max-w-3xl bg-[#F5F0E8] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" role="dialog" aria-modal="true" aria-labelledby="learn-more-title" data-id="element-77">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-[#1A1A1A]/10 shrink-0 bg-[#F5F0E8] z-10" data-id="element-78">
              <div data-id="element-79">
                <h2 id="learn-more-title" className="font-serif text-2xl text-[#1A1A1A]" data-id="element-80">
                  About the Fund
                </h2>
              </div>
              <button onClick={onClose} className="p-2 text-[#1A1A1A]/60 hover:text-[#1A1A1A] transition-colors" aria-label="Close modal" data-id="element-81">
                <XIcon size={24} strokeWidth={1.5} data-id="element-82" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-16" data-id="element-83">
              {/* Mission */}
              <section data-id="element-84">
                <h3 className="text-sm font-medium tracking-[0.2em] text-[#B8860B] uppercase mb-6" data-id="element-85">
                  Our Mission
                </h3>
                <div className="space-y-4 text-lg text-[#1A1A1A]/80 leading-relaxed font-serif" data-id="element-86">
                  <p data-id="element-87">
                    The Jabotinsky Legacy Fund was established to ensure that
                    the intellectual, political, and cultural contributions of
                    Ze'ev Jabotinsky remain a guiding force for future
                    generations. For too long, his vast body of work—spanning
                    political theory, novels, poetry, and journalism—has been
                    inaccessible to the broader English-speaking world.
                  </p>
                  <p data-id="element-88">
                    We believe that his synthesis of robust national defense and
                    uncompromising classical liberalism provides the essential
                    blueprint for a secure, dignified, and democratic future. By
                    preserving his archives, translating his writings, and
                    funding modern policy research, we are actively championing
                    the principles of the Iron Wall and <em data-id="element-89">Hadar</em>{' '}
                    (dignity).
                  </p>
                </div>
              </section>

              {/* Allocation */}
              <section data-id="element-90">
                <h3 className="text-sm font-medium tracking-[0.2em] text-[#B8860B] uppercase mb-8" data-id="element-91">
                  How Your Support Is Used
                </h3>
                <div className="space-y-8" data-id="element-92">
                  {allocations.map((item, index) => <div key={item.title} data-id="element-93">
                      <div className="flex justify-between items-end mb-2" data-id="element-94">
                        <h4 className="font-serif text-xl text-[#1A1A1A]" data-id="element-95">
                          {item.title}
                        </h4>
                        <span className="font-serif text-xl text-[#B8860B]" data-id="element-96">
                          {item.percentage}%
                        </span>
                      </div>
                      <p className="text-[#1A1A1A]/70 text-sm mb-3" data-id="element-97">
                        {item.description}
                      </p>
                      {/* Progress Bar */}
                      <div className="h-2 w-full bg-[#1A1A1A]/10 rounded-full overflow-hidden" data-id="element-98">
                        <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${item.percentage}%`
                  }} transition={{
                    duration: 1,
                    delay: 0.2 + index * 0.1,
                    ease: 'easeOut'
                  }} className="h-full bg-[#B8860B]" data-id="element-99" />
                      </div>
                    </div>)}
                </div>
              </section>

              {/* Essential Readings */}
              <section data-id="element-100">
                <h3 className="text-sm font-medium tracking-[0.2em] text-[#B8860B] uppercase mb-6" data-id="element-101">
                  Essential Readings
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-102">
                  {readings.map(book => <div key={book.title} className="p-5 border border-[#1A1A1A]/10 bg-white/40" data-id="element-103">
                      <div className="flex items-start justify-between mb-2" data-id="element-104">
                        <h4 className="font-serif text-lg text-[#1B2A4A] font-medium pr-2" data-id="element-105">
                          {book.title}
                        </h4>
                        <BookOpenIcon size={16} className="text-[#B8860B]/50 shrink-0 mt-1" strokeWidth={1.5} data-id="element-106" />
                      </div>
                      <div className="flex items-center gap-2 text-xs font-medium tracking-wider uppercase text-[#1A1A1A]/50 mb-3" data-id="element-107">
                        <span data-id="element-108">{book.year}</span>
                        <span data-id="element-109">&bull;</span>
                        <span data-id="element-110">{book.type}</span>
                      </div>
                      <p className="text-sm text-[#1A1A1A]/70 leading-relaxed" data-id="element-111">
                        {book.description}
                      </p>
                    </div>)}
                </div>
              </section>

              {/* Commitment */}
              <section className="bg-[#EBE5D9] p-6 md:p-8 border-l-2 border-[#B8860B]" data-id="element-112">
                <h3 className="font-serif text-xl text-[#1A1A1A] mb-3" data-id="element-113">
                  Our Commitment to Transparency
                </h3>
                <p className="text-[#1A1A1A]/70 text-sm leading-relaxed" data-id="element-114">
                  The Jabotinsky Legacy Fund operates as a registered non-profit
                  organization. We are committed to complete financial
                  transparency and rigorous governance. An annual report
                  detailing all expenditures, grants, and operational costs is
                  made available to all our patrons and guardians.
                </p>
              </section>
            </div>

            {/* Footer / Actions */}
            <div className="p-6 border-t border-[#1A1A1A]/10 bg-[#EBE5D9]/50 flex flex-col sm:flex-row justify-end items-center gap-4 shrink-0" data-id="element-115">
              <button onClick={onClose} className="w-full sm:w-auto px-6 py-3 text-[#1A1A1A]/70 hover:text-[#1A1A1A] font-medium tracking-wide transition-colors" data-id="element-116">
                Close
              </button>
              <button onClick={onOpenSupport} className="w-full sm:w-auto px-8 py-3 bg-[#1B2A4A] text-[#F5F0E8] font-medium tracking-wide hover:bg-[#1B2A4A]/90 transition-colors" data-id="element-117">
                Support the Legacy
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}